import type { ButtonProps } from '@chakra-ui/react';
import { Button, Flex, HStack, Spinner } from '@chakra-ui/react';
import { useIsFetching } from '@tanstack/react-query';
import { FeatureToggle } from '@ui/components/FeatureToggle';
import { MaskIcon } from '@ui/components/MaskIcon';
import { useAgents } from '@ui/hooks/api/useAgents';

import { SearchInputToggle } from './SearchInputToggle';
import { useAgentsView } from './useAgentsView';

export const AgentTableFilter = () => {
  const isGraduated = useAgentsView((s) => s.isGraduated);
  const sortBy = useAgentsView((s) => s.sortBy);
  const isFetching = useIsFetching([useAgents.queryKey]);

  return (
    <Flex
      flexDir={{
        base: 'column',
        desktop: 'row',
      }}
      gap={{
        base: '8px',
        desktop: '24px',
      }}
      flexWrap="wrap"
      flex="1"
    >
      <HStack flexWrap="wrap" gap="4px">
        {[null, true, false].map((value) => {
          return (
            <TableButton
              key={String(value)}
              icon="filter"
              isActive={isGraduated === value}
              onClick={() => {
                useAgentsView.setState({
                  isGraduated: value,
                });
              }}
            >
              {value === null && 'All'}
              {value === true && 'Graduated'}
              {value === false && 'Pre-graduation'}
            </TableButton>
          );
        })}
      </HStack>
      <HStack flexWrap="wrap">
        {(['market_cap', 'created_at'] as const).map((value) => {
          return (
            <TableButton
              key={String(value)}
              icon="sorter"
              isActive={sortBy === value}
              onClick={() => {
                useAgentsView.setState({
                  sortBy: value,
                });
              }}
            >
              {value === 'market_cap' && 'Market cap'}
              {value === 'created_at' && 'Latest'}
            </TableButton>
          );
        })}
        <HStack
          flex="1"
          flexDir={{
            desktop: 'row-reverse',
          }}
        >
          {!!isFetching && (
            <Spinner ml={2} color="secondary.persianBlue" boxSize="16px" />
          )}
          <FeatureToggle flag="searchAgents">
            <SearchInputToggle />
          </FeatureToggle>
        </HStack>
      </HStack>
    </Flex>
  );
};

const TableButton = ({
  isActive,
  icon,
  ...props
}: ButtonProps & {
  icon: 'filter' | 'sorter';
  isActive: boolean;
}) => {
  const dataActiveProp = isActive
    ? {
        'data-active': isActive,
      }
    : {};
  return (
    <Button variant="chip" {...dataActiveProp} {...props}>
      {isActive && (
        <MaskIcon
          icon={
            icon === 'filter'
              ? 'url(/masks/filter.svg)'
              : 'url(/masks/sorter.svg)'
          }
          bg="currentcolor"
          boxSize="16px"
        />
      )}
      {props.children}
    </Button>
  );
};
